/**
 * Minimal stateful read cursor over a `Uint8Array` used by the SHX
 * parser. Intentionally self-contained — no deps so the parser package
 * stays pure binary / pure computation.
 *
 * All reads that would exceed the buffer throw a plain object with the
 * shape `{ code: "truncated", offset: number, message: string }` — the
 * parser catches this and converts it into a `ShxParseResult` failure.
 */

/**
 * Error thrown by `Cursor` when a read would extend past the buffer.
 * Kept as a plain object (not an `Error` subclass) so it serialises
 * cleanly across any future worker boundaries and is cheap to match
 * with structural equality in tests.
 */
export type CursorTruncationError = {
  code: "truncated";
  offset: number;
  message: string;
};

export function isCursorTruncationError(
  value: unknown,
): value is CursorTruncationError {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { code?: unknown }).code === "truncated"
  );
}

export class Cursor {
  private readonly view: DataView;
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  /** Current absolute read position in bytes. */
  position(): number {
    return this.offset;
  }

  /** Number of unread bytes remaining. */
  remaining(): number {
    return this.bytes.byteLength - this.offset;
  }

  /** Total buffer size. */
  length(): number {
    return this.bytes.byteLength;
  }

  private ensure(n: number): void {
    if (this.offset + n > this.bytes.byteLength) {
      throw {
        code: "truncated",
        offset: this.offset,
        message: `Cannot read ${n} byte(s) at offset ${this.offset} (buffer ${this.bytes.byteLength})`,
      } satisfies CursorTruncationError;
    }
  }

  readUint8(): number {
    this.ensure(1);
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value;
  }

  readInt8(): number {
    this.ensure(1);
    const value = this.view.getInt8(this.offset);
    this.offset += 1;
    return value;
  }

  readUint16LE(): number {
    this.ensure(2);
    const value = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return value;
  }

  readUint32LE(): number {
    this.ensure(4);
    const value = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return value;
  }

  /**
   * Read `n` bytes, advancing the cursor. Returns a fresh `Uint8Array`
   * that aliases the underlying buffer (no copy). Throws
   * `CursorTruncationError` if the range is out of bounds.
   */
  readBytes(n: number): Uint8Array {
    this.ensure(n);
    const slice = this.bytes.subarray(this.offset, this.offset + n);
    this.offset += n;
    return slice;
  }

  /**
   * Read an ASCII string terminated by either a `\0` byte or `maxLen`
   * bytes (whichever comes first). The cursor stops **on** the null
   * byte — callers are expected to `skipBytes(1)` to consume it.
   */
  readNullTerminatedAscii(maxLen: number): string {
    const start = this.offset;
    const end = Math.min(this.offset + maxLen, this.bytes.byteLength);
    let stop = end;
    for (let i = this.offset; i < end; i++) {
      if (this.bytes[i] === 0) {
        stop = i;
        break;
      }
    }
    let ascii = "";
    for (let i = start; i < stop; i++) {
      ascii += String.fromCharCode(this.bytes[i] as number);
    }
    this.offset = stop;
    return ascii;
  }

  /** Advance the cursor by `n` bytes without reading. */
  skipBytes(n: number): void {
    this.ensure(n);
    this.offset += n;
  }

  /**
   * Absolute positioning. Used by the v1.0 header path where the
   * `readNullTerminatedAscii` overshoot needs to be rewound to a known
   * byte offset before reading the shape records. Throws
   * `CursorTruncationError` if the target is out of bounds.
   */
  seek(absolute: number): void {
    if (absolute < 0 || absolute > this.bytes.byteLength) {
      throw {
        code: "truncated",
        offset: absolute,
        message: `seek(${absolute}) out of bounds for buffer of ${this.bytes.byteLength}`,
      } satisfies CursorTruncationError;
    }
    this.offset = absolute;
  }

  /**
   * Advance the cursor forward while `predicate(byte)` returns `true`.
   * Stops at EOF or at the first byte that fails the predicate.
   */
  skipWhile(predicate: (byte: number) => boolean): void {
    while (
      this.offset < this.bytes.byteLength &&
      predicate(this.bytes[this.offset] as number)
    ) {
      this.offset += 1;
    }
  }

  /**
   * Peek `n` bytes ahead without advancing. Returns a fresh
   * `Uint8Array` view; out-of-bounds truncates silently.
   */
  peekBytes(n: number): Uint8Array {
    const end = Math.min(this.offset + n, this.bytes.byteLength);
    return this.bytes.subarray(this.offset, end);
  }
}
